/**
 * F 226 — TRASH. The shared mechanics of soft delete over a key-value store.
 *
 * WHY A RESERVED NAMESPACE AND NOT A FLAG ON THE VALUE
 *
 * The obvious design is to write `deletedAt` into the value. It does not
 * work here. `StorageAdapter` stores `any` under a string key, and the
 * live keyspace is not only entities: `freshcards:theme` is the string
 * "light", `freshcards:layout` is a mode name. A trash flag on a value
 * either cannot be attached to a string or silently changes the shape of
 * every value that IS an object — and a shape change is a migration,
 * which this feature explicitly forbids.
 *
 * So soft delete MOVES the value to a reserved sibling key and leaves
 * the value byte-identical:
 *
 *     freshcards:content:p1        live
 *     freshcards:content:p1::trash trashed, with a deletedAt sidecar
 *
 * Consequences, all of them the point:
 *   - no stored value changes shape
 *   - no adapter needs a schema change or a version bump
 *   - `delete` keeps its hard meaning and its callers are untouched
 *   - restore is a move back, so it cannot half-apply
 *   - a trashed key is absent from the live keyspace, so every existing
 *     read already excludes it without a single line changing
 *
 * The one thing the namespace cannot do by itself is filter an ARRAY
 * held under a single key — `freshcards:pages` is one key holding many
 * pages. That is why the page layer also sets `deletedAt` on the page
 * object itself. Both halves are needed and they are not redundant.
 */

/** Reserved suffix. Chosen so it cannot collide with a real key. */
export const TRASH_SUFFIX = '::trash';

export function trashKey(key: string): string {
  return `${key}${TRASH_SUFFIX}`;
}

export function isTrashKey(key: string): boolean {
  return key.endsWith(TRASH_SUFFIX);
}

export function liveKey(key: string): string {
  return isTrashKey(key) ? key.slice(0, -TRASH_SUFFIX.length) : key;
}

export interface TrashRecord {
  /** The original live key, so restore knows where to put it back. */
  key: string;
  /** ISO 8601. F 226 — mirrors `deletedAt` on the entity. */
  deletedAt: string;
  /** The value, byte-identical to what was live. */
  value: unknown;
}

export function nowIso(): string {
  return new Date().toISOString();
}

/**
 * The soft-delete / restore pair, expressed against the two primitives
 * every StorageAdapter already has. Adapters opt in by calling these
 * from their own methods rather than reimplementing the namespace.
 */
export interface TrashPrimitives {
  save(key: string, value: any, options?: any): Promise<void>;
  load(key: string, options?: any): Promise<any>;
  delete(key: string, options?: any): Promise<void>;
  list(prefix: string, options?: any): Promise<string[]>;
}

export async function softDeleteVia(
  adapter: TrashPrimitives,
  key: string,
  options?: any,
): Promise<void> {
  if (isTrashKey(key)) return; // already trashed; not an error
  const value = await adapter.load(key, options);
  if (value === undefined || value === null) return; // nothing to trash
  const record: TrashRecord = { key: liveKey(key), deletedAt: nowIso(), value };
  await adapter.save(trashKey(key), record, options);
  await adapter.delete(key, options);
}

export async function restoreVia(
  adapter: TrashPrimitives,
  key: string,
  options?: any,
): Promise<void> {
  const tKey = isTrashKey(key) ? key : trashKey(key);
  const record = (await adapter.load(tKey, options)) as TrashRecord | undefined;
  if (!record || typeof record !== 'object' || !('value' in record)) return;
  await adapter.save(liveKey(key), record.value, options);
  await adapter.delete(tKey, options);
}

/** Every trashed record under `prefix`, newest first. */
export async function listTrashVia(
  adapter: TrashPrimitives,
  prefix = '',
  options?: any,
): Promise<TrashRecord[]> {
  const keys = await adapter.list(prefix, options);
  const out: TrashRecord[] = [];
  for (const k of keys) {
    if (!isTrashKey(k)) continue;
    const rec = (await adapter.load(k, options)) as TrashRecord | undefined;
    if (rec && typeof rec === 'object' && 'deletedAt' in rec) out.push(rec);
  }
  return out.sort((a, b) => (a.deletedAt < b.deletedAt ? 1 : -1));
}
